import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { signAvatar } from "@/lib/avatar.server";
import { bytesMatchMime } from "@/lib/image-sniff";

const PROFILE_COLS =
  "display_name, avatar_url, home_city, bio, date_of_birth, terms_accepted_at, created_at, membership_tier, member_number, welcome_email_sent_at";


const avatarUploadSchema = z.object({
  fileName: z.string().trim().min(1).max(200),
  mimeType: z.enum(["image/jpeg", "image/png", "image/webp", "image/gif"]),
  dataUrl: z.string().trim().startsWith("data:").max(3_000_000),
});

async function withSignedAvatar<T extends { avatar_url: string | null } | null>(
  row: T,
): Promise<T> {
  if (!row) return row;
  const signed = await signAvatar(row.avatar_url);
  return { ...row, avatar_url: signed } as T;
}

export const getMyProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;

    const { data: authUser, error: authUserError } = await supabaseAdmin.auth.admin.getUserById(userId);
    if (authUserError) {
      const message = (authUserError.message ?? "").toLowerCase();
      if (message.includes("not found") || message.includes("user not found")) {
        return null;
      }
      // Upstream outage / empty error: degrade to "no profile yet" instead of crashing the page.
      if (
        !message ||
        /<!doctype html|web server is down|5\d\d:|fetch failed|econnrefused/i.test(message)
      ) {
        console.error("getMyProfile: auth lookup unavailable", message.slice(0, 200));
        return null;
      }
      throw new Error(authUserError.message);
    }

    if (!authUser?.user) return null;

    const { data, error } = await supabase
      .from("profiles")
      .select(PROFILE_COLS)
      .eq("user_id", userId)
      .maybeSingle();

    if (error) throw new Error(error.message);

    if (!data) {
      const { data: inserted, error: insErr } = await supabase
        .from("profiles")
        .insert({ user_id: userId })
        .select(PROFILE_COLS)
        .single();
      if (insErr) {
        const message = insErr.message.toLowerCase();
        if (message.includes("profiles_user_id_fkey")) {
          return null;
        }
        throw new Error(insErr.message);
      }
      return await withSignedAvatar(inserted);
    }

    return await withSignedAvatar(data);
  });

export const updateMyProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        display_name: z.string().trim().min(1).max(80).nullable().optional(),
        // Accept storage path (e.g. `<userId>/file.png`) or absolute URL up to 500 chars.
        avatar_url: z.string().trim().min(1).max(500).nullable().optional(),
        home_city: z.string().trim().max(80).nullable().optional(),
        bio: z.string().trim().max(280).nullable().optional(),
        date_of_birth: z
          .string()
          .regex(/^\d{4}-\d{2}-\d{2}$/, "use YYYY-MM-DD")
          .nullable()
          .optional(),
        accept_terms: z.boolean().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const { accept_terms, ...rest } = data;
    const updatePayload: Record<string, unknown> = { ...rest };
    if (accept_terms) {
      updatePayload.terms_accepted_at = new Date().toISOString();
    }

    const { data: updated, error } = await supabase
      .from("profiles")
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .update(updatePayload as any)
      .eq("user_id", userId)
      .select(PROFILE_COLS)
      .single();

    if (error) throw new Error(error.message);
    return await withSignedAvatar(updated);
  });

export const uploadMyAvatar = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => avatarUploadSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { userId } = context;

    const match = data.dataUrl.match(/^data:([^;]+);base64,(.+)$/);
    if (!match) throw new Error("Invalid image payload");
    if (match[1] !== data.mimeType) throw new Error("Image type mismatch");

    const extByType: Record<"image/jpeg" | "image/png" | "image/webp" | "image/gif", string> = {
      "image/jpeg": "jpg",
      "image/png": "png",
      "image/webp": "webp",
      "image/gif": "gif",
    };

    const bytes = Buffer.from(match[2], "base64");
    const maxBytes = 400 * 1024; // client compresses to ~100 KB; keep headroom
    if (!bytes.length || bytes.length > maxBytes) {
      throw new Error("Image too large after compression");
    }
    // The declared MIME type is client-supplied — verify the actual bytes.
    if (!bytesMatchMime(new Uint8Array(bytes), data.mimeType)) {
      throw new Error("That file is not a valid image");
    }

    const path = `${userId}/${Date.now()}-${crypto.randomUUID()}.${extByType[data.mimeType]}`;
    const { error } = await supabaseAdmin.storage.from("avatars").upload(path, bytes, {
      contentType: data.mimeType,
      cacheControl: "3600",
      upsert: false,
    });

    if (error) throw new Error(error.message);

    const signedUrl = await signAvatar(path);
    return { path, signedUrl };
  });

